import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit3, Trash2, RefreshCw, Ticket, CheckCircle2, Ban, TrendingUp } from 'lucide-react';
import {
  adminApi,
  formatCurrency,
  formatPercent,
  formatDate,
  formatNumber,
  extractErrorMessage,
} from '@kardume/services';
import { theme } from '@kardume/theme';
import type { CouponDto, CouponRequestDto, CouponStatus, Tone } from '@kardume/types';
import {
  PageHeader,
  Button,
  Badge,
  StatCard,
  Table,
  TableContainer,
  ActionButton,
  SearchInput,
  Select,
  Pagination,
  TextInput,
  CurrencyInput,
  Modal,
  ConfirmModal,
} from '@kardume/ui';
import { useNotification } from '@kardume/hooks';
import { useAsync } from '../utils/useAsync';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import DataState from '../components/DataState';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
`;

const Code = styled.span`
  font-family: 'Courier New', monospace;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${(p) => p.theme.primary};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.9rem;
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const FieldLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  font-family: inherit;
  resize: vertical;
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.primary}22;
  }
`;

const COUPON_STATUS_LABELS: Record<CouponStatus, string> = {
  ACTIVE: 'Ativo',
  PAUSED: 'Pausado',
  EXPIRED: 'Expirado',
  SOLD_OUT: 'Esgotado',
};

const STATUS_FILTER = [
  { value: '', label: 'Todos os status' },
  { value: 'ACTIVE', label: 'Ativo' },
  { value: 'PAUSED', label: 'Pausado' },
  { value: 'EXPIRED', label: 'Expirado' },
  { value: 'SOLD_OUT', label: 'Esgotado' },
];

/** Map a coupon status to a Badge tone. */
const statusTone = (status: CouponStatus): Tone => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'PAUSED':
      return 'warning';
    case 'EXPIRED':
      return 'danger';
    default:
      return 'secondary';
  }
};

/** Human-readable discount value for a coupon (percent or currency). */
const discountLabel = (coupon: CouponDto): string => {
  if (coupon.discountPercent != null) return formatPercent(coupon.discountPercent);
  if (coupon.discountValue != null) return formatCurrency(coupon.discountValue);
  return '-';
};

const emptyForm: CouponRequestDto = {
  code: '',
  title: '',
  description: '',
  discountPercent: undefined,
  discountValue: undefined,
  maxQuantity: 0,
  rules: '',
  startsAt: '',
  endsAt: '',
};

const CouponsPage: React.FC = () => {
  const { showNotification } = useNotification();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const debouncedSearch = useDebouncedValue(search.trim(), 350);

  const { data, loading, error, reload } = useAsync(
    () =>
      adminApi.listCoupons({
        page,
        size: pageSize,
        query: debouncedSearch || undefined,
        status: statusFilter ? (statusFilter as CouponStatus) : undefined,
      }),
    [page, pageSize, debouncedSearch, statusFilter]
  );

  const summaryState = useAsync(() => adminApi.getCouponSummary(), []);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CouponDto | null>(null);
  const [form, setForm] = useState<CouponRequestDto>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<CouponDto | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const rows = useMemo<CouponDto[]>(() => data?.content ?? [], [data]);
  const summary = summaryState.data;

  const reloadAll = () => {
    reload();
    summaryState.reload();
  };

  const onSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };
  const onStatus = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };
  const onPageSize = (size: number) => {
    setPageSize(size);
    setPage(0);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (coupon: CouponDto) => {
    setEditing(coupon);
    setForm({
      promotionId: coupon.promotionId,
      code: coupon.code,
      title: coupon.title,
      description: coupon.description,
      discountPercent: coupon.discountPercent,
      discountValue: coupon.discountValue,
      maxQuantity: coupon.maxQuantity,
      rules: coupon.rules,
      startsAt: coupon.startsAt.slice(0, 10),
      endsAt: coupon.endsAt.slice(0, 10),
    });
    setModalOpen(true);
  };

  const update = <K extends keyof CouponRequestDto>(key: K, value: CouponRequestDto[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (editing) {
        await adminApi.updateCoupon(editing.id, form);
        showNotification('Cupom atualizado com sucesso.', 'success');
      } else {
        await adminApi.createCoupon(form);
        showNotification('Cupom criado com sucesso.', 'success');
      }
      setModalOpen(false);
      reloadAll();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar o cupom.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await adminApi.deleteCoupon(deleting.id);
      showNotification('Cupom removido.', 'success');
      setDeleting(null);
      reloadAll();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível remover o cupom.'), 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Cupons"
        subtitle="Gere e controle os cupons de desconto da sua loja."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Novo cupom
          </Button>
        }
      />

      <StatsGrid>
        <StatCard
          label="Total de cupons"
          value={formatNumber(summary?.total ?? 0)}
          icon={<Ticket size={18} />}
        />
        <StatCard
          label="Ativos"
          value={formatNumber(summary?.active ?? 0)}
          icon={<CheckCircle2 size={18} />}
          iconColor={theme.success}
        />
        <StatCard
          label="Esgotados"
          value={formatNumber(summary?.soldOut ?? 0)}
          icon={<Ban size={18} />}
          iconColor={theme.warning}
        />
        <StatCard
          label="Usos totais"
          value={formatNumber(summary?.totalRedemptions ?? 0)}
          icon={<TrendingUp size={18} />}
          iconColor={theme.info}
        />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por código ou título..."
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
        <Select options={STATUS_FILTER} value={statusFilter} onChange={(e) => onStatus(e.target.value)} />
        <ActionButton title="Atualizar" onClick={reloadAll}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={!data || rows.length === 0}
        emptyTitle="Nenhum cupom encontrado"
        emptyDescription="Crie um cupom para oferecer descontos aos seus clientes."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Código</th>
                <th>Título</th>
                <th>Utilização</th>
                <th>Desconto</th>
                <th>Status</th>
                <th>Período</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((coupon) => (
                <tr key={coupon.id}>
                  <td>
                    <Code>{coupon.code}</Code>
                  </td>
                  <td>{coupon.title}</td>
                  <td>
                    {coupon.usedQuantity} / {coupon.maxQuantity}
                  </td>
                  <td>{discountLabel(coupon)}</td>
                  <td>
                    <Badge tone={statusTone(coupon.status)}>{COUPON_STATUS_LABELS[coupon.status]}</Badge>
                  </td>
                  <td>
                    {formatDate(coupon.startsAt)} – {formatDate(coupon.endsAt)}
                  </td>
                  <td>
                    <Actions>
                      <ActionButton title="Editar" onClick={() => openEdit(coupon)}>
                        <Edit3 size={16} />
                      </ActionButton>
                      <ActionButton title="Remover" color={theme.error} onClick={() => setDeleting(coupon)}>
                        <Trash2 size={16} />
                      </ActionButton>
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>

        <Pagination
          currentPage={page + 1}
          totalPages={data?.totalPages ?? 1}
          totalItems={data?.totalElements}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={onPageSize}
        />
      </DataState>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar cupom' : 'Novo cupom'}
        width="640px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" isLoading={saving} onClick={handleSubmit}>
              {editing ? 'Salvar alterações' : 'Criar cupom'}
            </Button>
          </>
        }
      >
        <FormGrid>
          <TextInput
            label="Código"
            value={form.code}
            onChange={(e) => update('code', e.target.value.toUpperCase())}
            placeholder="Ex.: KARDUME10"
          />
          <TextInput
            label="Título"
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            placeholder="Ex.: 10% na primeira compra"
          />
          <FullRow>
            <FieldGroup>
              <FieldLabel>Descrição</FieldLabel>
              <TextArea
                value={form.description ?? ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => update('description', e.target.value)}
                placeholder="Descreva o cupom para o cliente"
                rows={2}
              />
            </FieldGroup>
          </FullRow>
          <TextInput
            label="Desconto (%)"
            type="number"
            value={form.discountPercent ?? ''}
            onChange={(e) =>
              update('discountPercent', e.target.value === '' ? undefined : Number(e.target.value))
            }
          />
          <CurrencyInput
            label="Valor do desconto"
            value={form.discountValue}
            onChange={(value) => update('discountValue', value)}
          />
          <TextInput
            label="Quantidade máxima"
            type="number"
            value={form.maxQuantity}
            onChange={(e) => update('maxQuantity', Number(e.target.value))}
          />
          <TextInput
            label="Início"
            type="date"
            value={form.startsAt}
            onChange={(e) => update('startsAt', e.target.value)}
          />
          <TextInput
            label="Término"
            type="date"
            value={form.endsAt}
            onChange={(e) => update('endsAt', e.target.value)}
          />
          <FullRow>
            <FieldGroup>
              <FieldLabel>Regras de uso</FieldLabel>
              <TextArea
                value={form.rules ?? ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => update('rules', e.target.value)}
                placeholder="Ex.: Válido apenas para pedidos acima de R$ 50"
                rows={2}
              />
            </FieldGroup>
          </FullRow>
        </FormGrid>
      </Modal>

      <ConfirmModal
        isOpen={!!deleting}
        danger
        title="Remover cupom"
        message={
          <>
            Tem certeza que deseja remover o cupom <strong>{deleting?.code}</strong>? Esta ação não pode ser
            desfeita.
          </>
        }
        confirmLabel="Remover"
        loading={deleteLoading}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />
    </>
  );
};

export default CouponsPage;
